import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { login, isLockedOut, getLockoutTimeRemaining } from '../../services/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff, FiShield, FiAlertCircle } from 'react-icons/fi';

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: pulse 8s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.3; }
    50% { transform: scale(1.2); opacity: 0.5; }
  }
`;

const Wrapper = styled.div`
  max-width: 480px;
  width: 100%;
  background: #fff;
  padding: 48px 40px;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  position: relative;
  z-index: 1;
  
  @media (max-width: 600px) {
    padding: 40px 24px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  
  svg {
    color: white;
    font-size: 40px;
  }
`;

const Title = styled.h1`
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #718096;
  font-size: 15px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  position: relative;
`;

const InputLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 14px;
  color: #a0aec0;
  display: flex;
  align-items: center;
  
  svg {
    font-size: 18px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 14px 14px 44px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 15px;
  transition: all 0.3s ease;
  background: #f7fafc;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    background: #fff;
    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #cbd5e0;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 14px;
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s;
  
  &:hover {
    color: #667eea;
  }
  
  svg {
    font-size: 18px;
  }
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #c53030;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-left: 4px solid #c53030;
  
  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const InfoMessage = styled.div`
  background: #bee3f8;
  color: #2c5282;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-left: 4px solid #3182ce;
  
  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const WarningMessage = styled.div`
  background: #feebc8;
  color: #c05621;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-left: 4px solid #dd6b20;
  
  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    background: linear-gradient(135deg, #a0aec0 0%, #cbd5e0 100%);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

const Footer = styled.div`
  margin-top: 24px;
  text-align: center;
  color: #718096;
  font-size: 13px;
`;

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState('');
  const [warning, setWarning] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [lockoutTimer, setLockoutTimer] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for session expiry message
  useEffect(() => {
    if (searchParams.get('session') === 'expired') {
      setWarning('Your session has expired. Please log in again.');
    }
  }, [searchParams]);

  // Check if account is locked on mount and set up countdown
  useEffect(() => {
    if (isLockedOut()) {
      const remaining = getLockoutTimeRemaining();
      setLockoutTimer(remaining);
      setErr(`Too many failed login attempts. Please try again in ${Math.ceil(remaining / 60)} minute${Math.ceil(remaining / 60) > 1 ? 's' : ''}.`);
      
      // Update countdown every second
      const interval = setInterval(() => {
        const newRemaining = getLockoutTimeRemaining();
        if (newRemaining > 0) {
          setLockoutTimer(newRemaining);
          const minutes = Math.ceil(newRemaining / 60);
          setErr(`Too many failed login attempts. Please try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`);
        } else {
          setErr('');
          setLockoutTimer(0);
          clearInterval(interval);
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, []);

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password strength indicator
  const getPasswordStrength = (pass) => {
    if (pass.length < 6) return { strength: 'weak', color: '#c53030' };
    if (pass.length < 8) return { strength: 'medium', color: '#dd6b20' };
    if (pass.length >= 8 && /[A-Z]/.test(pass) && /[0-9]/.test(pass)) {
      return { strength: 'strong', color: '#38a169' };
    }
    return { strength: 'medium', color: '#dd6b20' };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setWarning('');
    setInfo('');
    
    // Check if locked out
    if (isLockedOut()) {
      const remaining = getLockoutTimeRemaining();
      const minutes = Math.ceil(remaining / 60);
      setErr(`Account is locked. Please wait ${minutes} minute${minutes > 1 ? 's' : ''}.`);
      return;
    }
    
    // Validation
    if (!email || !password) {
      setErr('Please fill in all fields');
      return;
    }

    // Email format validation
    if (!validateEmail(email)) {
      setErr('Please enter a valid email address');
      return;
    }

    // Password length validation
    if (password.length < 6) {
      setErr('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      setInfo('Login successful! Redirecting...');
      
      // Small delay to show success message
      setTimeout(() => {
        navigate('/admin');
      }, 500);
    } catch (e) {
      setErr(e.message || 'Invalid credentials. Please try again.');
      
      // Check if now locked out after this attempt
      if (isLockedOut()) {
        const remaining = getLockoutTimeRemaining();
        setLockoutTimer(remaining);
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormDisabled = loading || lockoutTimer > 0;

  return (
    <PageWrapper>
      <Wrapper>
        <Header>
          <IconWrapper>
            <FiShield />
          </IconWrapper>
          <Title>Admin Portal</Title>
          <Subtitle>Sign in to access the dashboard</Subtitle>
        </Header>
        
        <Form onSubmit={onSubmit}>
          {warning && (
            <WarningMessage>
              <FiAlertCircle />
              <div>{warning}</div>
            </WarningMessage>
          )}
          
          {info && (
            <InfoMessage>
              <FiAlertCircle />
              <div>{info}</div>
            </InfoMessage>
          )}
          
          <InputGroup>
            <InputLabel htmlFor="email">Email Address</InputLabel>
            <InputWrapper>
              <InputIcon>
                <FiMail />
              </InputIcon>
              <Input 
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email} 
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                disabled={isFormDisabled}
                maxLength={100}
              />
            </InputWrapper>
          </InputGroup>
          
          <InputGroup>
            <InputLabel htmlFor="password">Password</InputLabel>
            <InputWrapper>
              <InputIcon>
                <FiLock />
              </InputIcon>
              <Input 
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={isFormDisabled}
                maxLength={100}
              />
              <PasswordToggle 
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={isFormDisabled}
                tabIndex={-1}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </PasswordToggle>
            </InputWrapper>
          </InputGroup>
          
          {err && (
            <ErrorMessage>
              <FiAlertCircle />
              <div>{err}</div>
            </ErrorMessage>
          )}
          
          <Button type="submit" disabled={isFormDisabled}>
            {loading ? 'Signing in...' : lockoutTimer > 0 ? `Locked (${Math.ceil(lockoutTimer / 60)}m)` : 'Sign In'}
          </Button>
        </Form>
        
        <Footer>
          🔒 Protected admin area • Secure access only
        </Footer>
      </Wrapper>
    </PageWrapper>
  );
}
